import { DialogContentOptions } from "@omnia/types"
import { ReactNode, useState } from "react"
import { toDate } from "../utils/date"
import { Checkbox } from "./checkbox"
import { DialogBody } from "./DialogBody"
import { DialogFooter, DialogFooterButtons } from "./DialogFooter"
import { DialogHeader } from "./DialogHeader"
import { Msg } from "./Msg"

export type TermsAndConditionsDialogProps = PropsWithHtml | PropsWithContent

interface Props extends DialogContentOptions<boolean> {
    /**
     * The label to display against the "Accept" checkbox. (Something like "I have read and agree to the terms and conditions")
     * @default "I have read and agree to the terms and conditions"
     */
    checkboxLabel?: ReactNode
    /**
     * If specified, the dialog will show the terms, but not the "Agree" buttons.
     */
    readOnly?: boolean
    /**
     * If specified, and `readOnly: true`, will display "Accepted by X on Y" message underneath the terms.
     */
    acceptedBy?: {
        name: string
        timestamp: string
    }
}

interface PropsWithHtml extends Props {
    contentHtml: string
    content?: never
}

interface PropsWithContent extends Props {
    contentHtml?: never
    content: ReactNode
}

export const TermsAndConditionsDialog = ({
    close,
    contentHtml,
    content,
    readOnly,
    acceptedBy,
    checkboxLabel = <Msg>I have read and agree to the terms and conditions</Msg>,
}: TermsAndConditionsDialogProps) => {
    const [accepted, setAccepted] = useState<boolean>(Boolean(readOnly && acceptedBy))

    return (
        <div className="omnia-terms-and-conditions-dialog">
            <DialogHeader icon="fas fa-file-signature" help={false}>
                <Msg>Terms &amp; Conditions</Msg>
            </DialogHeader>

            <DialogBody className="omnia-terms-and-conditions-dialog--body">
                <div className="omnia-manage-partners-dialog--intro">
                    <p>
                        <Msg>To continue, please read and agree to the terms and conditions:</Msg>
                    </p>
                </div>
                <div className="omnia-terms-and-conditions-dialog--terms">
                    {contentHtml ? <div dangerouslySetInnerHTML={{ __html: contentHtml }}></div> : null}
                    {content}
                </div>
            </DialogBody>

            {!readOnly || accepted ? (
                <DialogFooter className="omnia-terms-and-conditions-dialog--acceptance">
                    <Checkbox checked={accepted} setChecked={setAccepted} disabled={readOnly}>
                        {checkboxLabel}
                    </Checkbox>
                    {acceptedBy ? (
                        <p>
                            <Msg
                                text="Accepted by {{name}} on {{timestamp}}"
                                args={{ name: acceptedBy.name, timestamp: formatTimestamp(acceptedBy.timestamp) }}
                            />
                        </p>
                    ) : null}
                </DialogFooter>
            ) : null}
            <DialogFooter>
                <DialogFooterButtons
                    canCommit={accepted}
                    hasChanges={!readOnly}
                    saveButtonText={<Msg>Accept &amp; Continue</Msg>}
                    onClose={() => close(false)}
                    onCommit={() => close(true)}
                />
            </DialogFooter>
        </div>
    )
}

function formatTimestamp(timestamp: string) {
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "long",
        timeStyle: "short",
    }).format(toDate(timestamp))
}
